import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { clsx } from 'clsx'
import { format } from 'date-fns'

import { getVehicleResourceKey } from '@dfds-pax/utils'

import { isSogRoute } from '../../Utils/sog-helpers'

import { useRoutes } from '../../Hooks/routes/useRoutes'
import { useVehicles } from '../../Hooks/vehicles/useVehicles'

import { ActiveSelectField, useActiveSelectField } from '../../context/ActiveSelectFieldProvider'
import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { TrackMultipleVehicles } from '../../Utils/tracking'
import { getMultipleVehicleSelectedCount } from '../../Utils/vehicles'
import SelectField from '../shared/SelectField'
import { VehicleIcon } from './VehicleIcon'
import Vehicles from './Vehicles'

const VehiclesSelector = () => {
  const vehicleTriggerRef = useRef<HTMLButtonElement>(null)
  const restoreTriggerFocusRef = useRef(false)
  const { dispatch, state } = useTravelSearchContext()
  const { activeField, closeIfActive, toggleField } = useActiveSelectField()
  const { t } = useTranslation()
  const { isRoutesLoading, selectedRoute } = useRoutes()
  const { isVehiclesLoading, selectedVehicle, vehicleApiData } = useVehicles()
  const outboundDate = state.selectedDates?.outboundDate ? format(state.selectedDates.outboundDate, 'yyyy-MM-dd') : undefined

  const isOpen = activeField === ActiveSelectField.VEHICLE

  useEffect(() => {
    if (isOpen || !restoreTriggerFocusRef.current) return
    restoreTriggerFocusRef.current = false
    vehicleTriggerRef.current?.focus()
  }, [isOpen])

  return (
    <div
      className={clsx(
        'col-span-12 box-border h-[58px] w-full justify-center md:col-span-6 lg:flex-1',
        isSogRoute(selectedRoute?.routeCode ?? '', outboundDate) && 'hidden'
      )}
    >
      <SelectField
        className="lg:border-r-0"
        dataAttrList={[
          { name: 'selected-vehicle', value: `${selectedVehicle?.code}` },
          {
            name: 'selected-trailer',
            value: `${selectedVehicle?.trailer?.code}`,
          },
        ]}
        disabled={
          vehicleApiData?.vehicleTypes?.reduce((count, current) => count + current.code.length, 0) === 1 ||
          (vehicleApiData?.vehicleTypes?.reduce((count, current) => count + current.code.length, 0) === 0 &&
            selectedVehicle.code === 'NCAR')
        }
        fieldName="vehicle"
        icon={selectedVehicle?.code ? <VehicleIcon code={selectedVehicle?.code} size="md" /> : null}
        isLoading={isRoutesLoading || isVehiclesLoading}
        isOpen={isOpen}
        onClick={(clickedOutside: boolean) => {
          !isRoutesLoading && !isVehiclesLoading && toggleField(ActiveSelectField.VEHICLE, clickedOutside)
        }}
        onClose={() => {
          closeIfActive(ActiveSelectField.VEHICLE)
        }}
        title={t('VEHICLE')}
        triggerRef={vehicleTriggerRef}
        value={
          selectedVehicle.trailer
            ? `${getMultipleVehicleSelectedCount(selectedVehicle.count)}${t(getVehicleResourceKey(selectedVehicle.code))} + ${getMultipleVehicleSelectedCount(selectedVehicle.count)} ${t(getVehicleResourceKey(selectedVehicle.trailer.code))}`
            : `${getMultipleVehicleSelectedCount(selectedVehicle.count)}${t(getVehicleResourceKey(selectedVehicle.code))}`
        }
        visibleRows={selectedVehicle && selectedVehicle.trailer ? 2 : 1}
      >
        <Vehicles
          dataAttrList={[
            { name: 'selected-vehicle', value: `${selectedVehicle?.code}` },
            {
              name: 'selected-trailer',
              value: `${selectedVehicle?.trailer?.code}`,
            },
          ]}
          isLoading={isRoutesLoading || isVehiclesLoading}
          onCheckMultipleVehicle={(multipleVehicleChecked: boolean) => {
            if (!multipleVehicleChecked && selectedVehicle) {
              // Reset vehicle count
              dispatch({
                type: 'SET_VEHICLE',
                vehicle: { ...selectedVehicle, count: 1 },
              })
            }
            dispatch({
              checked: multipleVehicleChecked,
              type: 'SET_MULTIPLE_VEHICLES_CHECKED',
            })
            TrackMultipleVehicles(multipleVehicleChecked)
          }}
          onEscape={() => {
            restoreTriggerFocusRef.current = true
            closeIfActive(ActiveSelectField.VEHICLE)
          }}
          onFooterClick={() => closeIfActive(ActiveSelectField.VEHICLE)}
        />
      </SelectField>
    </div>
  )
}
export default VehiclesSelector
